import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Slider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  Dot,
  DotProps,
} from 'recharts';
import {
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { MaintenanceHistory } from './MaintenanceHistory';
import { useCapabilities } from '../contexts/CapabilitiesContext';

interface MeterMaintenanceModalProps {
  open: boolean;
  onClose: () => void;
  meterProductId: string;
  meterId: string;
  productType: string;
  currentBaseline?: number;
  currentUCL?: number;
  currentLCL?: number;
  recentProvings?: Array<{
    date: string;
    meterFactor: number;
    reportId?: string;
  }>;
  onMaintenanceComplete: () => void;
}

interface PreviewLimits {
  ucl: number;
  lcl: number;
  center: number;
}

// Default tolerance is now 0.25% for all products
// Product-specific tolerances are configured in UserPreferences
const DEFAULT_TOLERANCE = 0.0025;  // ±0.25%

export const MeterMaintenanceModal: React.FC<MeterMaintenanceModalProps> = ({
  open,
  onClose,
  meterProductId,
  meterId,
  productType,
  currentBaseline,
  currentUCL,
  currentLCL,
  recentProvings = [],
  onMaintenanceComplete
}) => {
  const { can } = useCapabilities();  // RBAC: gate Reset Baseline on run_maintenance
  // Flow-aware product label. The API targets the real track via meterProductId
  // (e.g. "..._KJETA1_LOW"); productType is only the bare base ("KJETA1"), so for a
  // multi-flow track derive the HIGH/LOW/MID suffix and show "KJETA1 (LOW)" to avoid
  // any ambiguity about which track the baseline is being applied to.
  const flowMatch = /_(HIGH|LOW|MID\d*)$/i.exec(meterProductId || '');
  const productLabel = flowMatch ? `${productType} (${flowMatch[1].toUpperCase()})` : productType;

  const { getIdTokenClaims, user } = useAuth0();
  const [resetMethod, setResetMethod] = useState<'chart_selection' | 'manual_input'>('chart_selection');
  const [manualValue, setManualValue] = useState<string>(
    typeof currentBaseline === 'number' ? currentBaseline.toString() : '1.0000'
  );
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [supportingDocs, setSupportingDocs] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [tolerance, setTolerance] = useState<number>(DEFAULT_TOLERANCE);
  const [meterHistory, setMeterHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedProving, setSelectedProving] = useState<any>(null);
  const [recentMaintenances, setRecentMaintenances] = useState<any[]>([]);
  const [loadingMaintenances, setLoadingMaintenances] = useState(false);
  // Deleting an event from the history dialog changes this meter's baseline/health. Bump the key to refetch
  // the "Recent Baseline Resets" list in place, and remember that the meter itself must be reloaded on close
  // (the reset path reloads the page after its success message; a delete gets the same treatment).
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [meterChanged, setMeterChanged] = useState(false);

  // Fetch product-specific tolerance and meter history
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = (await getIdTokenClaims())?.__raw;

        // Fetch tolerance
        const toleranceResponse = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/product-pricing`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (toleranceResponse.ok) {
          const data = await toleranceResponse.json();
          const product = data.products?.find((p: any) => p.product_type === productType);
          if (product && product.tolerance) {
            setTolerance(product.tolerance);
          }
        }

        // Fetch meter history
        setLoadingHistory(true);
        const historyResponse = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          if (historyData.history && Array.isArray(historyData.history)) {
            // Format data for chart - sort by date to ensure chronological order (oldest to newest)
            const chartData = historyData.history
              .map((item: any) => ({
                date: item.proving_date,
                meterFactor: parseFloat(item.meter_factor),
                reportId: item.report_id,
                displayDate: new Date(item.proving_date).toLocaleDateString(),
              }))
              .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setMeterHistory(chartData);

            // Select most recent by default (last item after sorting)
            if (chartData.length > 0) {
              setSelectedProving(chartData[chartData.length - 1]);
            }
          }
        }
      } catch (err) {
        console.log('Error fetching data:', err);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (open && productType && meterProductId) {
      fetchData();

      // Fetch recent maintenances
      const fetchMaintenances = async () => {
        try {
          setLoadingMaintenances(true);
          const token = (await getIdTokenClaims())?.__raw;
          const response = await fetch(
            `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/maintenance-history`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            // Get only the last 5 baseline resets
            const baselineResets = (data.history || [])
              .filter((item: any) => item.action_type === 'baseline_reset')
              .slice(0, 5);
            setRecentMaintenances(baselineResets);
          }
        } catch (err) {
          console.log('Error fetching maintenances:', err);
        } finally {
          setLoadingMaintenances(false);
        }
      };

      fetchMaintenances();
    }
  }, [open, productType, meterProductId, getIdTokenClaims, historyRefreshKey]);

  // Calculate preview limits based on reset method
  const calculatePreviewLimits = useCallback((): PreviewLimits => {
    let newBaseline = typeof currentBaseline === 'number' ? currentBaseline : 1.0000;

    if (resetMethod === 'chart_selection' && selectedProving) {
      newBaseline = selectedProving.meterFactor;
    } else if (resetMethod === 'manual_input') {
      const parsed = parseFloat(manualValue);
      if (!isNaN(parsed)) {
        newBaseline = parsed;
      }
    }

    return {
      center: newBaseline,
      ucl: newBaseline + tolerance,
      lcl: newBaseline - tolerance
    };
  }, [resetMethod, manualValue, selectedProving, currentBaseline, tolerance]);

  const previewLimits = calculatePreviewLimits();

  const handleResetMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResetMethod(event.target.value as 'chart_selection' | 'manual_input');
  };

  // Memoize the click handler
  const handleDotClick = useCallback((payload: any) => {
    console.log('Selecting proving:', payload);
    setSelectedProving(payload);
  }, []);

  const handleManualValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow typing decimal numbers
    if (/^\d*\.?\d*$/.test(value)) {
      setManualValue(value);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSupportingDocs(Array.from(event.target.files));
    }
  };

  const validateInputs = (): boolean => {
    if (!reason || reason.length < 10) {
      setError('Please provide a reason (minimum 10 characters)');
      return false;
    }

    if (resetMethod === 'manual_input') {
      const value = parseFloat(manualValue);
      if (isNaN(value) || value < 0.9000 || value > 1.1000) {
        setError('Manual baseline must be between 0.9000 and 1.1000');
        return false;
      }
    }

    if (resetMethod === 'chart_selection' && !selectedProving) {
      setError('Please select a proving report from the chart');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmReset = async (e?: React.MouseEvent) => {
    // Prevent any default button behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Close the confirmation dialog but DON'T trigger any navigation yet
    setConfirmOpen(false);
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('Starting baseline reset process...');
      const token = (await getIdTokenClaims())?.__raw;

      // Prepare the request body
      const requestBody: any = {
        reset_method: resetMethod === 'chart_selection' ? 'selected_proving' : resetMethod,
        reason,
        notes,
        supporting_docs: [], // Will be populated after file uploads
        user_email: user?.email || 'unknown' // Include user email
      };

      if (resetMethod === 'manual_input') {
        requestBody.manual_value = parseFloat(manualValue);
      } else if (resetMethod === 'chart_selection' && selectedProving) {
        // Set the baseline to the exact proving the user clicked -- keep it a
        // 'selected_proving' reset (NOT manual) and send which point + its date so
        // the maintenance is anchored to that proving, not to today's date.
        requestBody.reset_method = 'selected_proving';
        requestBody.manual_value = selectedProving.meterFactor;
        requestBody.selected_report_id = selectedProving.reportId;
        requestBody.selected_date = selectedProving.date;
      }

      // Upload supporting documents first if any
      if (supportingDocs.length > 0) {
        const uploadedDocs: string[] = [];

        for (const file of supportingDocs) {
          try {
            // Step 1: Get presigned URL for upload
            const uploadUrlResponse = await fetch(
              `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/maintenance-docs`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  filename: file.name,
                  content_type: file.type || 'application/octet-stream',
                  maintenance_id: 'pending' // Will be replaced with actual maintenance_id
                }),
              }
            );

            if (!uploadUrlResponse.ok) {
              throw new Error(`Failed to get upload URL for ${file.name}`);
            }

            const uploadData = await uploadUrlResponse.json();
            console.log('Presigned POST response:', uploadData);

            // Step 2: Upload file to S3 using presigned POST
            // IMPORTANT: Fields must be added before the file
            const formData = new FormData();

            // Add all the fields from the presigned POST response
            if (uploadData.fields) {
              Object.keys(uploadData.fields).forEach(key => {
                formData.append(key, uploadData.fields[key]);
              });
            }

            // The file MUST be added last
            formData.append('file', file);

            const s3Response = await fetch(uploadData.upload_url, {
              method: 'POST',
              body: formData,
              redirect: 'manual', // Prevent automatic redirect handling
            });

            // S3 returns 204 No Content on successful upload
            if (s3Response.status !== 204 && s3Response.status !== 200) {
              const errorText = await s3Response.text();
              console.error('S3 upload error:', s3Response.status, errorText);
              throw new Error(`Failed to upload ${file.name} to S3: ${s3Response.status}`);
            }

            console.log(`Successfully uploaded ${file.name} to S3`);

            // Add S3 key to the list
            uploadedDocs.push(uploadData.s3_key);
          } catch (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            setError(`Failed to upload ${file.name}`);
            // Continue with other files
          }
        }

        requestBody.supporting_docs = uploadedDocs;
        console.log('Supporting docs uploaded:', uploadedDocs);
      }

      // Call the reset baseline API
      console.log('Calling reset baseline API with:', requestBody);
      console.log('API Endpoint:', `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/reset-baseline`);

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/meters/${meterProductId}/reset-baseline`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log('Reset baseline response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Reset baseline error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to reset baseline');
      }

      const result = await response.json();
      console.log('Reset baseline success:', result);
      setSuccess(true);

      // Show the success message briefly, then close and let the parent refetch this meter in place.
      // (Previously this reloaded the whole page, which threw away the dashboard's state and every other
      // in-flight fetch just to pick up one meter's new baseline.)
      setTimeout(() => {
        handleClose();
        onMaintenanceComplete();
      }, 2000);

    } catch (err) {
      console.error('Error resetting baseline - Full error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setResetMethod('chart_selection');
    setManualValue(currentBaseline?.toString() || '1.0000');
    setReason('');
    setNotes('');
    setSupportingDocs([]);
    setError(null);
    setSuccess(false);
    setConfirmOpen(false);
    setSelectedProving(null);
    setMeterHistory([]);
    onClose();
    if (meterChanged) {
      // An event was deleted while the modal was open: the control chart, health badge and Cost of
      // Non-Compliance are stale until this meter is re-read. Ask the parent to refetch — no page reload.
      setMeterChanged(false);
      onMaintenanceComplete();
    }
  };

  const formatMaintenanceDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';

    try {
      // Try parsing the timestamp
      const date = new Date(timestamp);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date timestamp:', timestamp);
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disablePortal={false}
        sx={{ zIndex: 1400 }} // Higher than background components
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <SettingsIcon color="primary" />
              <Typography variant="h6">Baseline Maintenance</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {success ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={3}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Baseline Successfully Reset
              </Typography>
              <Typography color="text.secondary">
                The baseline has been updated and will be reflected in all calculations.
              </Typography>
            </Box>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Meter: {meterId} - {productLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Resetting the baseline will update control limits for all future calculations.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box mb={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Reset Method</FormLabel>
                  <RadioGroup value={resetMethod} onChange={handleResetMethodChange}>
                    <FormControlLabel
                      value="chart_selection"
                      control={<Radio />}
                      label="Select from Historical Proving Reports"
                      disabled={meterHistory.length === 0}
                    />
                    {resetMethod === 'chart_selection' && (
                      <Box ml={4} mb={2} mt={2}>
                        {loadingHistory ? (
                          <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                          </Box>
                        ) : meterHistory.length > 0 ? (
                          <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Click on any point to select it as the new baseline
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart
                                  data={meterHistory}
                                  margin={{ top: 5, right: 30, left: 20, bottom: 50 }}
                                  onClick={(e: any) => {
                                    if (e && e.activePayload && e.activePayload[0]) {
                                      console.log('Chart clicked:', e.activePayload[0].payload);
                                      handleDotClick(e.activePayload[0].payload);
                                    }
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis
                                    dataKey="displayDate"
                                    angle={-45}
                                    textAnchor="end"
                                    height={100}
                                  />
                                  <YAxis
                                    domain={['dataMin - 0.005', 'dataMax + 0.005']}
                                    tickFormatter={(value) => value.toFixed(4)}
                                  />
                                  <RechartsTooltip
                                    formatter={(value: any) => value.toFixed(4)}
                                    labelFormatter={(label) => `Date: ${label}`}
                                  />
                                  {typeof currentBaseline === 'number' && (
                                    <ReferenceLine
                                      y={currentBaseline}
                                      stroke="#ff9800"
                                      strokeDasharray="5 5"
                                      label="Current Baseline"
                                    />
                                  )}
                                  {typeof currentUCL === 'number' && (
                                    <ReferenceLine
                                      y={currentUCL}
                                      stroke="#f44336"
                                      strokeDasharray="3 3"
                                      label="UCL"
                                    />
                                  )}
                                  {typeof currentLCL === 'number' && (
                                    <ReferenceLine
                                      y={currentLCL}
                                      stroke="#f44336"
                                      strokeDasharray="3 3"
                                      label="LCL"
                                    />
                                  )}
                                  <Line
                                    type="monotone"
                                    dataKey="meterFactor"
                                    stroke="#8884d8"
                                    strokeWidth={2}
                                    dot={(props: any) => {
                                      const { cx, cy, payload, index } = props;
                                      const isSelected = selectedProving &&
                                        selectedProving.date === payload.date &&
                                        selectedProving.meterFactor === payload.meterFactor;

                                      return (
                                        <g key={`dot-${index}`}>
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={10}
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDotClick(payload);
                                            }}
                                          />
                                          <circle
                                            cx={cx}
                                            cy={cy}
                                            r={isSelected ? 8 : 5}
                                            fill={isSelected ? '#2196f3' : '#8884d8'}
                                            stroke={isSelected ? '#1976d2' : 'none'}
                                            strokeWidth={2}
                                            pointerEvents="none"
                                          />
                                        </g>
                                      );
                                    }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </Paper>
                            {selectedProving && (
                              <Alert severity="info">
                                Selected: <strong>{selectedProving.meterFactor.toFixed(4)}</strong> from {selectedProving.displayDate}
                                {selectedProving.reportId && ` (Report: ${selectedProving.reportId})`}
                              </Alert>
                            )}
                          </>
                        ) : (
                          <Alert severity="warning">
                            No historical proving reports available for this meter
                          </Alert>
                        )}
                      </Box>
                    )}
                    <FormControlLabel
                      value="manual_input"
                      control={<Radio />}
                      label="Enter Manual Baseline Value"
                    />
                    {resetMethod === 'manual_input' && (
                      <Box ml={4} mt={1}>
                        <TextField
                          label="Manual Baseline Value"
                          value={manualValue}
                          onChange={handleManualValueChange}
                          variant="outlined"
                          size="small"
                          helperText="Must be between 0.9000 and 1.1000"
                          error={parseFloat(manualValue) < 0.9 || parseFloat(manualValue) > 1.1}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">MF:</InputAdornment>,
                          }}
                        />
                      </Box>
                    )}
                  </RadioGroup>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Recent Maintenance History Preview */}
              {recentMaintenances.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Recent Baseline Resets
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>New Baseline</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentMaintenances.map((maintenance: any) => (
                          <TableRow key={maintenance.maintenance_id}>
                            <TableCell>
                              {formatMaintenanceDate(maintenance.reference_date || maintenance.timestamp)}
                            </TableCell>
                            <TableCell>
                              {typeof maintenance.baseline_after === 'number'
                                ? maintenance.baseline_after.toFixed(4)
                                : maintenance.baseline_after}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={maintenance.reason}
                              >
                                {maintenance.reason}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {maintenance.performed_by?.split('@')[0] || 'Unknown'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box mb={3}>
                <TextField
                  label="Reason for Reset"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  required
                  variant="outlined"
                  helperText={`${reason.length}/10 minimum characters`}
                  error={reason.length > 0 && reason.length < 10}
                />
              </Box>

              <Box mb={3}>
                <TextField
                  label="Additional Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                />
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Supporting Documents
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Upload Documents
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileUpload}
                  />
                </Button>
                {supportingDocs.length > 0 && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {supportingDocs.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => setSupportingDocs(docs => docs.filter((_, i) => i !== index))}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Control Limits Preview
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="center">Current</TableCell>
                        <TableCell align="center">After Reset</TableCell>
                        <TableCell align="center">Change</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Baseline</TableCell>
                        <TableCell align="center">{typeof currentBaseline === 'number' ? currentBaseline.toFixed(4) : 'N/A'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {previewLimits.center.toFixed(4)}
                        </TableCell>
                        <TableCell align="center">
                          {typeof currentBaseline === 'number' ? ((previewLimits.center - currentBaseline) * 100).toFixed(2) + '%' : 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>UCL (±{(tolerance * 100).toFixed(2)}%)</TableCell>
                        <TableCell align="center">{typeof currentUCL === 'number' ? currentUCL.toFixed(4) : 'N/A'}</TableCell>
                        <TableCell align="center">{previewLimits.ucl.toFixed(4)}</TableCell>
                        <TableCell align="center">
                          {typeof currentUCL === 'number' ? (previewLimits.ucl - currentUCL).toFixed(4) : 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>LCL (±{(tolerance * 100).toFixed(2)}%)</TableCell>
                        <TableCell align="center">{typeof currentLCL === 'number' ? currentLCL.toFixed(4) : 'N/A'}</TableCell>
                        <TableCell align="center">{previewLimits.lcl.toFixed(4)}</TableCell>
                        <TableCell align="center">
                          {typeof currentLCL === 'number' ? (previewLimits.lcl - currentLCL).toFixed(4) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {typeof currentBaseline === 'number' && Math.abs(previewLimits.center - currentBaseline) > 0.005 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Significant Change:</strong> The new baseline differs by more than 0.5% from the current baseline.
                  Please ensure this is intentional.
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Button
              onClick={() => setShowHistory(true)}
              startIcon={<HistoryIcon />}
              disabled={loading}
            >
              View History
            </Button>
            <Box>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Tooltip title={can('run_maintenance') ? '' : 'Requires the Maintenance (run_maintenance) permission'}>
                <span>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading || !reason || reason.length < 10 || !can('run_maintenance')}
                    startIcon={loading ? <CircularProgress size={20} /> : <SettingsIcon />}
                    sx={{ ml: 1 }}
                  >
                    {loading ? 'Resetting...' : 'Reset Baseline'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </DialogActions>
        )}
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        sx={{ zIndex: 1450 }} // Higher than the main modal (1400)
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Confirm Baseline Reset
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You are about to reset the baseline for <strong>{meterId} - {productLabel}</strong>
          </Typography>
          <Typography gutterBottom>
            New Baseline: <strong>{previewLimits.center.toFixed(4)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will be recorded in the maintenance history and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReset}
            variant="contained"
            color="warning"
          >
            Confirm Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance History */}
      {showHistory && (
        <MaintenanceHistory
          meterProductId={meterProductId}
          meterId={meterId}
          productType={productType}
          onClose={() => setShowHistory(false)}
          embedded={false}
          onChanged={() => {                 // an event was deleted: refresh this modal's list, reload the meter on close
            setHistoryRefreshKey((k) => k + 1);
            setMeterChanged(true);
            onMaintenanceComplete();
          }}
        />
      )}
    </>
  );
};